/**
 * PWA install helpers.
 * - registerServiceWorker(): register /service-worker.js
 * - subscribeToInstallPrompt(cb): call cb(prompt) once the browser fires
 *   'beforeinstallprompt' (Chrome, Edge, Android). Cb receives an object
 *   with .prompt() to trigger the native install dialog.
 * - isStandalone(): true if the app is already installed / launched from
 *   the home screen.
 * - isIOS(): true on iPhone/iPad/iPod Safari (which does not fire
 *   beforeinstallprompt — we show manual instructions instead).
 */

export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ reports as Mac; detect via touch
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

let deferredPrompt = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((cb) => cb(e));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((cb) => cb(null));
  });
}

export function subscribeToInstallPrompt(cb) {
  listeners.add(cb);
  // fire immediately if we already captured it
  if (deferredPrompt) cb(deferredPrompt);
  return () => listeners.delete(cb);
}

export function getInstallPrompt() {
  return deferredPrompt;
}

export async function triggerInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice;
}
